import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import type { CustomAxiosRequestConfig } from 'axios-auth-refresh/dist/utils';
import {
  encodedUsername,
  destroyToken,
  setAuthToken,
  getAuthToken,
  username,
} from './utils/authentication';
import { log } from './utils/debugHelper';
import types, {
  CredentialsViewType,
  EmployerProfileAssessmentResponseViewType,
  EmployerProfileAssessmentViewType,
  JsonErrorType,
  MultiChoiceQuestionType,
  StaffProfileViewType,
  StaffResumeType,
  StaffResumeViewType,
  UserEmployerViewType,
} from './types/services-api';
import { ResumeSearchParametersType } from './context/JobPostingContext';
import { JobSearchParamsType } from './context/JobSearchContext';
import { PaymentPayloadType } from './components/purchase/BraintreeDropInPaymentForm';
import analytics, {
  ENGAGEMENT_CATEGORY,
  PUBLISH_POSTING_ACTION,
} from './analytics';
import { positionCodeToDisplayName } from './utils/displayHelper';
import {
  AmazonBedrockParseDocumentResponse,
  AmazonBedrockParseTextResponse,
  JDExtractorResponse,
  ResumeUpdatePayload,
  SaveParsedResumeResponse,
} from './types/amazon-bedrock';
import { ContactUsPayloadType } from './components/ContactForm';
import { BenchmarkResponse, ComplianceResponse } from './types/salary';
import { BasicAddressType } from './types/axios-requests';
import { SmsPreferencesData } from './components/account-settings/SmsPreferences';
import { CourseType } from './components/profile/CourseAddEditList';
import { CertType } from './components/profile/CertAddEditList';
import { EditJobType } from './components/job-postings/JobPostingEdit';
import {
  JobCreateResponseType,
  JobUpdateResponseType,
} from './components/job-postings/JobPostingForm';
import { AuthenticatedDpJobViewType } from './components/job-search/JobSearchJobDetail';
import { JobStatusType } from './constants';
import { UnauthenticatedDpJobViewType } from './templates/jobs/job-post-catch-all';

if (process.env.GATSBY_API_URL) {
  axios.defaults.baseURL = process.env.GATSBY_API_URL;
}

export const formatError = (error: {
  response?: AxiosResponse<any>;
  request?: XMLHttpRequest;
  message?: string;
}) => {
  let standardizedError = {
    message: '',
    data: null,
    ...error,
  };
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    log(`[api] error.response.data:`, error.response.data);
    log(`[api] error.response.status:`, error.response.status);
    log(`[api] error.response.headers:`, error.response.headers);
    standardizedError.data = error.response.data;
    standardizedError.message = `Error: The server responded with status code ${error.response.status}`;
    if (
      error.response.data &&
      error.response.data.error &&
      error.response.data.error.errors &&
      error.response.data.error.errors.length > 0 &&
      error.response.data.error.errors[0].reason !== 'paymentError'
    ) {
      standardizedError.message = error.response.data.error.errors[0].message;
    } else if (
      error.response.data &&
      error.response.data.error &&
      error.response.data.error.message
    ) {
      standardizedError.message = error.response.data.error.message;
    } else if (
      error.response.data &&
      typeof error.response.data.message === 'string'
    ) {
      // Flat error envelope: { success, statusCode, message, ... }
      standardizedError.message = error.response.data.message;
    }
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    log(`[api] error.request:`, error.request);
    standardizedError.message = `The request was made but no response was received`;
  } else {
    // Something happened in setting up the request that triggered an Error
    log('Error', error.message);
    standardizedError.message = `Something happened in setting up the request that triggered an error`;
  }
  // log(error.config);
  return standardizedError;
};

axios.interceptors.response.use(
  function (response) {
    const configData = {
      method: response.config.method,
      url: response.config.url,
      status: response.status,
      statusText: response.statusText,
    };
    log('[api]', configData);
    if (response.data) {
      log('[api]', response.data);
    }
    return response;
  },
  function (error) {
    return Promise.reject(formatError(error));
  }
);

// Since axios-auth-refresh automatically stalls additional requests while refreshing
// the token, it is a good idea to wrap your token logic interceptor in a
// function, to make sure the stalled requests are using the newly fetched token.
axios.interceptors.request.use(
  (config: InternalAxiosRequestConfig<any> & { skipAuth?: boolean }) => {
    config.headers = config.headers ?? {};
    const headers = config.headers as Record<string, string | undefined>;

    if (config.skipAuth) {
      delete headers.Authorization;
      delete headers.authorization;
      return config;
    }

    const authToken = getAuthToken();
    if (!!authToken) {
      headers.Authorization = authToken;
    }

    return config;
  }
);

const refreshAuthLogic = (failedRequest: { response: AxiosResponse<any> }) => {
  log('attemptToRefreshToken() called');

  const tokenAtRefreshStart =
    (typeof failedRequest.response?.config?.headers?.Authorization === 'string'
      ? failedRequest.response.config.headers.Authorization
      : undefined) || getAuthToken();

  // If there is no active auth session, skip refresh instead of calling
  // /auth/undefined/token and potentially clearing a newer login state.
  if (!tokenAtRefreshStart || !username()) {
    log('skip token refresh: no active auth session');
    return Promise.reject();
  }

  return Auth.refreshToken()
    .then((res) => {
      if (res.status === 201 && res.headers.authorization) {
        if (getAuthToken() !== tokenAtRefreshStart) {
          log('skip token refresh apply: auth session changed');
          return Promise.reject();
        }

        log('auth token refresh success');
        const token = res.headers.authorization;
        setAuthToken(token);
        failedRequest.response.config.headers['Authorization'] = token;
      } else {
        console.error('auth token refresh failed', res);
        if (getAuthToken() === tokenAtRefreshStart) {
          destroyToken();
        }
        return Promise.reject();
      }
    })
    .catch((err) => {
      console.error('error refreshing token', err.response);
      if (getAuthToken() === tokenAtRefreshStart) {
        destroyToken();
      }
      return Promise.reject(err);
    });
};

// Instantiate the interceptor to handle automatically refreshing the token
createAuthRefreshInterceptor(axios, refreshAuthLogic, {
  pauseInstanceWhileRefreshing: false, // default: false
});

const customAxiosRequestConfig: CustomAxiosRequestConfig = {
  skipAuthRefresh: true,
};

const authLoginAxiosRequestConfig: CustomAxiosRequestConfig & {
  skipAuth: boolean;
} = {
  skipAuthRefresh: true,
  skipAuth: true,
};

// These serverless parse endpoints are authenticated api9 routes
// (`/api9/parse/{document,text}/${encodedUsername()}`) — they must send the
// auth header like any other api9 call. Do NOT set skipAuth here: the request
// interceptor now actively strips Authorization when skipAuth is true.
const serverlessAxiosRequestConfig: AxiosRequestConfig<
  FormData | { prompt: string; username: string; text: string }
> = {};

/*
 * ------------------------------
 * Auth
 * ------------------------------
 */
const Auth = {
  login: function (body: CredentialsViewType) {
    return axios.post(
      `/api9/auth/login`,
      {
        ...body,
        legacy: false,
      },
      authLoginAxiosRequestConfig
    );
  },
  refreshToken: function () {
    return axios.post(
      `/api9/auth/${encodedUsername()}/token`,
      undefined,
      customAxiosRequestConfig
    );
  },
  // The token is attached here rather than by the request interceptor so the
  // caller can destroy the local session as soon as this returns.
  logout: function () {
    return axios.post(`/api9/auth/${encodedUsername()}/logout`, undefined, {
      ...customAxiosRequestConfig,
      headers: { Authorization: getAuthToken() },
    });
  },
  // Account deletion (Apple 5.1.1(v) / Play). Reuses the same production
  // endpoint the legacy Ionic app calls: an authenticated PUT to the bare user
  // resource with an empty body closes the account; the backend purges PII
  // within 30 days [Currently broken]. The caller signs the user out afterward.
  closeAccount: function () {
    return axios.put(`/api9/users/${encodedUsername()}`, {});
  },
  updateEmail: function (body: { email: string; password: string }) {
    return axios.put(`/api9/auth/${encodedUsername()}/email-address`, {
      ...body,
    });
  },
  updatePassword: function (body: {
    oldPassword: string;
    password: string;
    confirmationPassword: string;
  }) {
    return axios.post(`/api9/auth/${encodedUsername()}/password`, {
      ...body,
    });
  },
  updatePhone: function (body: { phone: string }) {
    return axios.put(`/api9/auth/${encodedUsername()}/phone`, body);
  },
  requestPasswordReset: function (body: { email: string; role: string }) {
    return axios.put(`/api9/auth/password`, {
      ...body,
    });
  },
  resetPasswordWithResetCode: function (body: {
    email: string;
    role: string;
    resetCode: string;
    newPassword: string;
  }) {
    return axios.post(`/api9/auth/password`, {
      ...body,
    });
  },
};

/*
 * ------------------------------
 * Staff
 * ------------------------------
 */
const Staff = {
  register: function (body: {
    firstName: string;
    lastName: string;
    email: string;
    confirmEmail: string;
    password: string;
    optin: boolean;
    utmSource: string;
    utmMedium: string;
    utmCampaign: string;
    utmContent: string;
  }) {
    return axios.post<types.RegistrationStaffTempViewType>(
      `/api9/users/staff/v2`,
      body
    );
  },
  current: function () {
    return axios.get<types.StaffProfileViewType>(
      `/api9/users/staff/${encodedUsername()}`
    );
  },
  uploadResume: function (file: File) {
    const body = new FormData();
    body.append('file', file);
    return axios.post<AmazonBedrockParseDocumentResponse>(
      `/api9/parse/document/${encodedUsername()}`,
      body,
      serverlessAxiosRequestConfig
    );
  },
  generateBio: function (body: {
    text: string;
    prompt: string;
    mode?: 'text';
  }) {
    return axios.post<AmazonBedrockParseTextResponse>(
      `/api9/parse/text/${encodedUsername()}`,
      body,
      serverlessAxiosRequestConfig
    );
  },
  saveResume: function (props: { body: ResumeUpdatePayload }) {
    return axios.post<SaveParsedResumeResponse>(
      `/api9/resume-builder/${encodedUsername()}`,
      props.body
    );
  },
  profile: function () {
    return axios.get<types.StaffResumeViewType>(
      `/api9/users/staff/${encodedUsername()}/resumes`
    );
  },
  applications: function () {
    return axios.get<types.JobApplicationViewType[]>(
      `/api9/users/staff/${encodedUsername()}/applications`
    );
  },
  invitations: function () {
    return axios.get<
      (types.JobApplicationViewType & types.JobInvitationViewType)[]
    >(`/api9/users/staff/${encodedUsername()}/invitations`);
  },
  getInvitation: function (invitationId: number) {
    return axios.get<
      types.JobApplicationViewType & types.JobInvitationViewType
    >(`/api9/users/staff/${encodedUsername()}/invitations/${invitationId}`);
  },
  blockEmployer: function (props: {
    isBlocked: boolean;
    employerUsername: string;
  }) {
    return axios.put(
      `/api9/users/staff/${encodedUsername()}/employers/${encodedUsername(props.employerUsername)}/block`,
      { isBlocked: props.isBlocked }
    );
  },
  declineInvitation: function (jobId: number, message: string) {
    return axios.put(
      `/api9/users/staff/${encodedUsername()}/invitations/${jobId}`,
      { message }
    );
  },
  applyToJob: function ({
    jobId,
    jobDates,
  }: {
    jobId: number;
    jobDates: string[];
  }) {
    return axios.post<types.JobApplicationViewType>(
      `/api9/users/staff/v2/${encodedUsername()}/applications/${jobId}`,
      { jobDates }
    );
  },
  getApplication: function ({ jobId }: { jobId: number }) {
    return axios.get<types.JobApplicationViewType>(
      `/api9/users/staff/${encodedUsername()}/applications/${jobId}`
    );
  },
  updateBasicInfo: function (props: {
    body: {
      name: string;
      phone: string;
      position: string;
      specialties: string;
    };
  }) {
    return axios.put<StaffProfileViewType>(
      `/api9/users/staff/${encodedUsername()}/basic-info`,
      props.body
    );
  },
  updateLocationInfo: function ({ body }: { body: BasicAddressType }) {
    return axios.put<types.StaffProfileViewType>(
      `/api9/users/staff/${encodedUsername()}/location-info`,
      body
    );
  },
  updateJobPreferences: function (body: {
    emailAlerts: string;
    positionCode: string;
    specialty: string;
    zip: string;
    username: string;
  }) {
    return axios.post(
      `/api9/users/staff/${encodedUsername(body.username)}/register`,
      body
    );
  },
  updateAlertPreferences: function (body: Partial<SmsPreferencesData>) {
    return axios.put(`/api9/preferences/${encodedUsername()}`, body);
  },
  updateEmailAlerts: function ({
    emailAlerts,
    radius,
  }: {
    emailAlerts: string;
    radius: number;
  }) {
    return axios.put(`/api9/users/staff/${encodedUsername()}/email-alerts`, {
      emailAlerts,
      radius,
    });
  },
  updateIntro: function (props: { body: { intro: string } }) {
    return axios.put<StaffResumeType>(
      `/api9/users/staff/${encodedUsername()}/resumes/intro`,
      { ...props.body }
    );
  },
  createCourse: function ({ body }: { body: CourseType }) {
    return axios.post(
      `/api9/users/staff/${encodedUsername()}/resumes/courses`,
      body
    );
  },
  updateCourse: function ({
    courseId,
    body,
  }: {
    courseId: number;
    body: CourseType;
  }) {
    return axios.put(
      `/api9/users/staff/${encodedUsername()}/resumes/courses/${courseId}`,
      body
    );
  },
  deleteCourse: function ({ courseId }: { courseId: number }) {
    return axios.delete(
      `/api9/users/staff/${encodedUsername()}/resumes/courses/${courseId}`
    );
  },
  createCert: function ({ body }: { body: CertType }) {
    return axios.post(
      `/api9/users/staff/${encodedUsername()}/resumes/certs`,
      body
    );
  },
  updateCert: function ({ certId, body }: { certId: number; body: CertType }) {
    return axios.put(
      `/api9/users/staff/${encodedUsername()}/resumes/certs/${certId}`,
      body
    );
  },
  deleteCert: function ({ certId }: { certId: number }) {
    return axios.delete(
      `/api9/users/staff/${encodedUsername()}/resumes/certs/${certId}`
    );
  },
  getStaffPremiumData: function () {
    return axios.get<types.UserPremiumInfoViewType>(
      `/api9/users/staff/${encodedUsername()}/resumes/premium-info`
    );
  },
  updateVisibility: function (body: { state: boolean }) {
    return axios.put<StaffResumeViewType>(
      `/api9/users/staff/${encodedUsername()}/resumes/visibility`,
      body
    );
  },
  updateRelocate: function (body) {
    return axios.put(
      `/api9/users/staff/${encodedUsername()}/resumes/relocate`,
      body
    );
  },
  updateAvailability: function (body) {
    return axios.put<StaffResumeViewType>(
      `/api9/users/staff/${encodedUsername()}/resumes/availability`,
      body
    );
  },
};

/*
 * ------------------------------
 * Employer
 * ------------------------------
 */

const Employer = {
  register: function (
    body: types.RegistrationEmployerViewType & {
      utmSource?: string;
      utmMedium?: string;
      utmCampaign?: string;
      utmContent?: string;
    }
  ) {
    return axios.post<UserEmployerViewType>(`/api9/users/employers`, {
      ...body,
    });
  },
  current: function () {
    return axios.get<types.UserEmployerViewType>(
      `/api9/users/employers/${encodedUsername()}`
    );
  },
  jobs: function () {
    return axios.get<types.JobOwnerSummaryViewType[]>(
      `/api9/users/employers/${encodedUsername()}/jobs?limit=2500`
    );
  },
  subscription: function (orderId: number) {
    return axios.get<types.ProductOrderViewType>(
      `/api9/users/employers/${encodedUsername()}/order/${orderId}`
    );
  },
  getBoosts: function () {
    return axios.get(`/api9/users/employers/${encodedUsername()}/boosts`);
  },
  applyBoost: function (jobId: number, boostId: number) {
    return axios.put(
      `/api9/users/employers/${encodedUsername()}/boost/${boostId}?job-id=${jobId}`
    );
  },
  editBoostSubscription: function (body: {
    orderId: number;
    nextNationalQuantity: number;
    nextDistroQuantity: number;
  }) {
    return axios.put(
      `/api9/users/employers/${encodedUsername()}/order/${body.orderId}/boosts`,
      {
        nextNationalQuantity: body.nextNationalQuantity,
        nextDistroQuantity: body.nextDistroQuantity,
      }
    );
  },
  jobSlots: function () {
    return axios.get<types.JobSlotType[]>(
      `/api9/users/employers/${encodedUsername()}/job-slots`
    );
  },
  inviteToApply: function (props: { signedUrl: string; message: string }) {
    return axios.post<types.InvitationResumeViewType>(`${props.signedUrl}`, {
      message: props.message,
    });
  },
  cancelInvite: function ({ jobId, username }) {
    return axios.put(
      `/api9/users/employers/${encodedUsername()}/jobs/${jobId}/invitations/${encodedUsername(
        username
      )}`
    );
  },
  profile: function () {
    return axios.get<types.EmployerProfileViewType>(
      `/api9/users/employers/${encodedUsername()}/profile`
    );
  },
  offices: function () {
    return axios.get<types.OfficeProfileViewType[]>(
      `/api9/users/employers/${encodedUsername()}/offices`
    );
  },
  officeDetail: function ({ officeId }) {
    return axios.get(
      `/api9/users/employers/${encodedUsername()}/offices/${officeId}`
    );
  },
  createOffice: function ({ body }) {
    return axios.post(
      `/api9/users/employers/${encodedUsername()}/offices`,
      body
    );
  },
  updateOffice: function ({ officeId, body }) {
    return axios.put(
      `/api9/users/employers/${encodedUsername()}/offices/${officeId}`,
      body
    );
  },
  updatePrimaryOffice: function ({ officeId }) {
    return axios.put<types.OfficeProfileViewType>(
      `/api9/users/employers/${encodedUsername()}/offices/${officeId}/primary`
    );
  },
  deleteOffice: function ({ officeId }) {
    return axios.delete(
      `/api9/users/employers/${encodedUsername()}/offices/${officeId}`
    );
  },
  defaultOfficePhoto: function (userId: number) {
    return (
      process.env.GATSBY_API_URL +
      `api9/photos/multi/${userId}/OFFICE_PHOTOS/0/0`
    );
  },
  uploadOfficePhoto: function (props: {
    officeId: number;
    description: string;
    photoFile: Blob;
    userId: number;
  }) {
    const body = new FormData();
    body.append('description', props.description);
    body.append('file', props.photoFile);
    return axios.post(
      `/api9/photos/multi/${props.userId}/OFFICE_PHOTOS/${props.officeId}`,
      body
    );
  },
  editOfficePhoto: function ({ signedPhotoUrl, description, photoFile }) {
    const body = new FormData();
    body.append('description', description);
    body.append('file', photoFile);
    return axios.put(signedPhotoUrl, body);
  },
  deleteOfficePhoto: function ({
    officeId,
    photoId,
    userId,
  }: {
    officeId: number;
    photoId: number;
    userId: number;
  }) {
    return axios.delete(
      `/api9/photos/multi/${userId}/OFFICE_PHOTOS/${officeId}/${photoId}`
    );
  },
  sortOfficePhotos: function ({
    officeId,
    ids,
    userId,
  }: {
    officeId: number;
    ids: { id: number }[];
    userId: number;
  }) {
    return axios.put(
      `/api9/photos/multi/${userId}/OFFICE_PHOTOS/${officeId}/order`,
      { ids: ids }
    );
  },
  insertOfficeHours: function ({ officeId, body }) {
    return axios.put(
      `/api9/users/employers/${encodedUsername()}/offices/${officeId}/hours`,
      body
    );
  },
  deleteOfficeHours: function ({ officeId, dow }) {
    return axios.delete(
      `/api9/users/employers/${encodedUsername()}/offices/${officeId}/hours/${dow}`
    );
  },
  updateName: function ({ body }) {
    return axios.put(
      `/api9/users/employers/${encodedUsername()}/contact-info`,
      body
    );
  },
  updatePublic: function ({ body }) {
    return axios.put(
      `/api9/users/employers/${encodedUsername()}/profile/public`,
      body
    );
  },
  updateAbout: function ({ about }) {
    return axios.put(
      `/api9/users/employers/${encodedUsername()}/profile/about`,
      {
        about,
      }
    );
  },
  updateCandidateInfo: function ({ state }) {
    return axios.put(
      `/api9/users/employers/${encodedUsername()}/profile/candidate`,
      {
        state,
      }
    );
  },
  applications: function () {
    return axios.get(`/api9/users/employers/${encodedUsername()}/applications`);
  },
  receipts: function () {
    return axios.get<types.PaymentReceiptType[]>(
      `/api9/users/employers/${encodedUsername()}/payment/receipts`
    );
  },
  getJob: function (jobId: number) {
    return axios.get<types.JobOwnerDetailViewType>(
      `/api9/users/employers/${encodedUsername()}/jobs/${jobId}`
    );
  },
  publishJob: function (jobId: number, slotId?: number) {
    return axios
      .put<types.JobOwnerDetailViewType>(
        `/api9/users/employers/${encodedUsername()}/jobs/${jobId}/publish${
          !!slotId ? `/?slot-id=${slotId}` : ''
        }`
      )
      .then((res) => {
        analytics.Events.trackEvent({
          actionType: PUBLISH_POSTING_ACTION,
          category: ENGAGEMENT_CATEGORY,
          label: PUBLISH_POSTING_ACTION,
          payload: {
            publishPostingFormFields: {
              job_id: res.data.id,
              position: positionCodeToDisplayName[res.data.position],
              isConfidential: res.data.isConfidential,
              city: res.data.city,
              state: res.data.state,
              zip: res.data.zip,
              salary: res.data.salary,
              salaryUnits: res.data.salaryUnits,
            },
            msa_key: res.data.zip,
          },
        });
        return res;
      });
  },
  updateJob: function (props: { jobId: number; body: EditJobType }) {
    return axios.put<JobUpdateResponseType>(
      `/api9/employers/${encodedUsername()}/jobs/${props.jobId}`,
      props.body
    );
  },
  subscriptions: function () {
    return axios.get(
      `/api9/users/employers/${encodedUsername()}/subscriptions`
    );
  },
  matching: function () {
    return axios.get(
      `/api9/users/employers/${encodedUsername()}/match-criteria`
    );
  },
  getCoupon: function ({ sku, couponCode }) {
    return axios.get(
      `/api9/users/employers/${encodedUsername()}/coupons/${sku}/${couponCode}`
    );
  },
  applyCoupon: function ({ zip, position, category, couponCode }) {
    return axios.get(
      `/api9/users/employers/${encodedUsername()}/catalog/${zip}/${position}/${category}?coupon-code=${couponCode}`
    );
  },
  upgradeResume: function ({ zip, position }) {
    return axios.get(
      `/api9/users/employers/${encodedUsername()}/catalog/${zip}/${position}/UPGRADE_RESUME`
    );
  },
  paymentToken: function () {
    return axios.get<types.PaymentTokenViewType>(
      `/api9/users/${encodedUsername()}/payment/token`
    );
  },
  getReceipt: function (paymentId: string) {
    return axios.get<types.PaymentReceiptType>(
      `/api9/users/employers/${encodedUsername()}/payment/${paymentId}/receipt`
    );
  },
  getInterviewOutcome: function ({ interviewUrl }) {
    return axios.get<types.UserInterviewViewType>(interviewUrl);
  },
  updateInterviewOutcome: function ({ interviewUrl, body }) {
    return axios.put<types.UserInterviewViewType>(interviewUrl, body);
  },
};

/*
 * ------------------------------
 * User
 * ------------------------------
 */
const User = {
  updateEmailOptouts: function (optouts) {
    return axios.put(`/api9/users/${encodedUsername()}/optout2`, {
      ...optouts,
    });
  },
  generateLead: function (userData) {
    return axios.put(`/api9/users/public/lead`, { ...userData });
  },
  uploadProfilePhoto: function ({
    signedPhotoUrl,
    photoFile,
  }: {
    signedPhotoUrl: string;
    photoFile: Blob;
  }) {
    const body = new FormData();
    body.append('file', photoFile);
    return axios.post(signedPhotoUrl, body);
  },
  editProfilePhoto: function ({ signedPhotoUrl, photoFile }) {
    const body = new FormData();
    body.append('file', photoFile);
    return axios.put(signedPhotoUrl, body);
  },
  deleteProfilePhoto: function ({ signedPhotoUrl }) {
    return axios.delete(signedPhotoUrl);
  },
  getEmployer: function ({ employer }) {
    return axios.get(
      `/api9/users/${encodedUsername()}/employer/${encodedUsername(employer)}`
    );
  },
  getInterviews: function ({ date }) {
    return axios.get<types.UserInterviewViewType[]>(
      `/api9/users/${encodedUsername()}/interviews`,
      {
        params: { date },
      }
    );
  },
  // getInterviews: function ({ date }) {
  //   return axios.get<
  //     | ServicesApiTypes.EmployerInterviewResponseType[]
  //     | ServicesApiTypes.StaffInterviewResponseType[]
  //   >(`/api9/users/${encodedUsername()}/interviews`, { params: { date } });
  // },
};
/*
 * ------------------------------
 * Jobs
 * ------------------------------
 */

const Jobs = {
  getPricing: function ({
    orderId,
    jobQuantity,
    inviteQuantity,
    nationalQuantity,
    distroQuantity,
  }: {
    orderId: number;
    jobQuantity: number;
    inviteQuantity: number;
    nationalQuantity: number;
    distroQuantity: number;
  }) {
    return axios.get<types.SubscriptionPreviewViewType>(
      `/api9/users/employers/${encodedUsername()}/order/${orderId}/subscription-preview`,
      {
        params: {
          jobQuantity,
          inviteQuantity,
          nationalQuantity,
          distroQuantity,
        },
      }
    );
  },
  catalog: function (discountCode?: string) {
    return axios.get<types.CatalogViewType>(
      `/api9/users/employers/${encodedUsername()}/catalog`,
      {
        params: {
          'discount-code': discountCode,
        },
      }
    );
  },
  publicCatalog: function (discountCode?: string) {
    return axios.get<types.CatalogViewType>(`/api9/prepay/catalog`, {
      params: {
        'discount-code': discountCode,
      },
    });
  },
  paymentToken: function () {
    return axios.get<types.PaymentTokenViewType>(`/api9/prepay/token`);
  },
  prepay: function (body: PaymentPayloadType, isEmployer: boolean) {
    const params = !!body.discountCode
      ? {
          params: {
            'discount-code': body.discountCode,
          },
        }
      : undefined;
    const api = isEmployer
      ? `/api9/users/employers/${encodedUsername()}/payment`
      : `/api9/prepay/payment`;
    return axios.post<
      types.PaymentResultsViewType | { data: { error: JsonErrorType } }
    >(
      api,
      {
        ...body,
      },
      params
    );
  },
  addOnPayment: function (body: PaymentPayloadType) {
    const { jobId } = body;
    const params = !!body.discountCode
      ? {
          params: {
            'discount-code': body.discountCode,
          },
        }
      : undefined;
    const api = `/api9/users/employers/${encodedUsername()}/jobs/${jobId}/payment`;
    return axios.post<types.PaymentResultsViewType>(
      api,
      {
        ...body,
      },
      params
    );
  },
  editSubscription: function (props: {
    orderId: number;
    body: {
      nonce?: string;
      cancellationNote?: string;
      cancellationReason?: string;
      email?: string;
      fee?: number;
      name?: string;
      nextFee?: number;
      phone?: string;
      products?: {
        productCode: string;
        quantity: number;
        discountCode?: string;
      }[];
      jobSlotIds?: number[];
    };
  }) {
    return axios.put<types.PaymentResultsViewType>(
      `/api9/users/employers/${encodedUsername()}/order/${props.orderId}`,
      props.body
    );
  },
  create: function (body: EditJobType) {
    return axios.post<JobCreateResponseType>(
      `/api9/employers/${encodedUsername()}/jobs`,
      body
    );
  },
  get: function ({ jobId }: { jobId: number | string }) {
    return axios.get<
      AuthenticatedDpJobViewType | types.PartnerJobDetailViewType
    >(`/api9/users/${encodedUsername()}/jobs/${jobId}`);
  },
  applications: function (props: { jobId: number; matching: boolean }) {
    return axios.get<
      (types.EmployerAppSummaryViewType | types.PartnerAppSummaryViewType)[]
    >(
      `/api9/users/employers/${encodedUsername()}/applications/${props.jobId}${
        props.matching ? '/matches' : ''
      }`
    );
  },
  invitations: function ({ jobId }) {
    return axios.get(
      `/api9/users/employers/${encodedUsername()}/jobs/${jobId}/invitations`
    );
  },
  getJobApplicationDetail: function ({ jobId, applicantUsername, matchId }) {
    //when iterable encodes a username, there's no need to encode when calling the api
    const username = applicantUsername.includes('%')
      ? applicantUsername
      : encodedUsername(applicantUsername);
    return axios.get<types.EmployerResumeViewType>(
      `/api9/users/employers/${encodedUsername()}/applications/${jobId}/${username}`,
      {
        params: {
          match: matchId,
        },
      }
    );
  },
  search: function (searchParams: JobSearchParamsType) {
    return axios.get<
      types.JobResponseViewType<
        AuthenticatedDpJobViewType[],
        types.PartnerJobDetailViewType[]
      >
    >(`/api9/users/${encodedUsername()}/jobs`, { params: searchParams });
  },
  getPublic: function ({ jobId }: { jobId: number | string }) {
    return axios.get<
      types.JobResponseViewType<
        UnauthenticatedDpJobViewType[],
        types.PartnerJobDetailViewType[]
      >
    >(`/api9/jobs/${jobId}`);
  },
  redeem: function ({ jobId, productCode }) {
    return axios.post(
      `/api9/users/employers/${encodedUsername()}/jobs/${jobId}/redeem`,
      {
        product: productCode,
      }
    );
  },
  view: function (props: {
    jobId: string | number;
    body?: {};
    isAuthenticated: boolean;
  }) {
    if (props.isAuthenticated) {
      return axios.put(
        `/api9/users/${encodedUsername()}/jobs/${props.jobId}/view`,
        props.body
      );
    } else {
      return axios.put(`/api9/jobs/${props.jobId}/view`, props.body);
    }
  },
  updateOpinion: function ({ jobId, body }: { jobId: string; body: any }) {
    return axios.put<AuthenticatedDpJobViewType>(
      `/api9/users/${encodedUsername()}/jobs/${jobId}/opinion`,
      body
    );
  },
  updateJobStatus: function ({
    jobId,
    body,
  }: {
    jobId: number;
    body: { status: JobStatusType; feedback?: string };
  }) {
    return axios.put<types.JobOwnerDetailViewType>(
      `/api9/users/employers/${encodedUsername()}/jobs/${jobId}/status`,
      body
    );
  },
  updateJobDateStatus: function (props: {
    jobId: number;
    jobDateId: number;
    body: { status: string };
  }) {
    return axios.put<{ jobDates: types.JobDateType[] }>(
      `/api9/users/employers/${encodedUsername()}/jobs/${props.jobId}/status/${
        props.jobDateId
      }`,
      props.body
    );
  },
  getUserJobFlag: function ({ jobId }) {
    return axios.get(`/api9/users/${encodedUsername()}/jobs/${jobId}/flag/`);
  },
  updateUserJobFlag: function ({ jobId, body }) {
    return axios.put(
      `/api9/users/${encodedUsername()}/jobs/${jobId}/flag`,
      body
    );
  },
};
/*
 * ------------------------------
 * Resumes
 * ------------------------------
 */
const Resumes = {
  searchPreview: function (props: { position: string; zip: string }) {
    const { position, zip } = props;
    return axios.get<types.EmployerResumeResponseViewType>(
      `/api9/users/employers/${encodedUsername()}/resumes/search/${position}/${zip}`
    );
  },
  preview: function (props: { position: string; zip: string }) {
    const { position, zip } = props;
    return axios.get(`/api9/prepay/resumes/${position}/${zip}`);
  },
  search: function (props: {
    jobId: number;
    searchParams: ResumeSearchParametersType;
    preview?: boolean;
  }) {
    const { jobId, searchParams, preview } = props;
    return axios.get<types.EmployerResumeResponseViewType>(
      `/api9/users/employers/${encodedUsername()}/resumes/${jobId}/search`,
      {
        params: {
          ...searchParams,
          limit: searchParams.limit.toString(),
          offset: searchParams.offset.toString(),
          preview: !!preview ? 1 : 0,
        },
      }
    );
  },
  getResumeDetailValidated: function ({ validatedUrl }) {
    return axios.get(validatedUrl);
  },
  purchase: function ({
    nonce,
    productCode,
    positionCode,
    zip,
    redeemWithPoints,
    couponCode,
    campaignId,
    templateId,
    jobId,
  }) {
    return axios.post(
      `/api9/users/employers/${encodedUsername()}/subscriptions`,
      {
        nonce,
        product: productCode,
        position: positionCode,
        zip,
        redeemWithPoints,
        upgradedId: jobId,
      },
      {
        params: {
          'coupon-code': couponCode,
          'campaign-id': campaignId,
          'template-id': templateId,
        },
      }
    );
  },
  addFeedbackLabel: function ({ feedbackUrl, feedback }) {
    return axios.post(feedbackUrl, { feedback });
  },
  removeFeedbackLabel: function ({ feedbackUrl, feedback }) {
    return axios.delete(feedbackUrl, { data: { feedback } });
  },
  updateFeedbackNote: function ({ resumeUsername, note }) {
    return axios.put(
      `/api9/users/employers/${encodedUsername()}/resumes/${encodedUsername(
        resumeUsername
      )}/feedback-notes`,
      { note }
    );
  },
  updateOpinion: function ({ resumeUsername, opinion }) {
    return axios.put<types.OpinionUpdateViewType>(
      `/api9/users/employers/${encodedUsername()}/resumes/${encodedUsername(
        resumeUsername
      )}/opinion`,
      { ...opinion }
    );
  },
};

/*
 * ------------------------------
 * Candidate Matching
 * ------------------------------
 */
const CandidateMatching = {
  get: function ({ jobId }: { jobId: number }) {
    return axios.get(
      `/api9/users/employers/${encodedUsername()}/match-criteria/${jobId}`
    );
  },
  create: function ({
    jobId,
    criteria,
  }: {
    jobId: number;
    criteria: types.MatchCriteriaViewType;
  }) {
    return axios.post<types.MatchCriteriaType>(
      `/api9/users/employers/${encodedUsername()}/match-criteria/${jobId}`,
      criteria
    );
  },
  update: function ({
    jobId,
    criteria,
  }: {
    jobId: number;
    criteria: types.MatchCriteriaViewType;
  }) {
    return axios.put<types.MatchCriteriaType>(
      `/api9/users/employers/${encodedUsername()}/match-criteria/${jobId}`,
      criteria
    );
  },
  search: function (props: {
    jobId: number;
    searchParams: ResumeSearchParametersType;
    preview?: boolean;
  }) {
    return axios.get(
      `/api9/users/employers/${encodedUsername()}/resumes/${
        props.jobId
      }/matches`,
      { params: { ...props.searchParams, preview: !!props.preview ? 1 : 0 } }
    );
  },
};
/*
 * ------------------------------
 * Conversations
 * ------------------------------
 */
// Omit the param entirely when the caller doesn't know, so the API keeps its
// own fallback rather than being told the wrong thing.
function conversationTypeParam(isInvite?: boolean) {
  return isInvite === undefined ? {} : { type: isInvite ? 'invite' : 'app' };
}

const Conversations = {
  get: function ({ limit, offset }) {
    return axios.get<types.UserConversationViewType[]>(
      `/api9/users/${encodedUsername()}/conversations`,
      {
        params: {
          limit,
          offset,
        },
      }
    );
  },
  getGroup: function (props: { limit: number; offset: number }) {
    return axios.get<types.UserConversationGroupViewType[]>(
      `/api9/users/${encodedUsername()}/convo-groups`,
      {
        params: {
          limit: props.limit,
          offset: props.offset,
        },
      }
    );
  },
  // A conversation id is either an application id or an invite id, and the two
  // are independent sequences whose ranges overlap -- so the same number can be
  // valid as both for one user. Tell the API which kind we hold whenever we
  // know, otherwise it has to guess (DP-2280).
  getById: function (props: { conversationId: number; isInvite?: boolean }) {
    return axios.get<types.UserConversationGroupViewType>(
      `/api9/users/${encodedUsername()}/conversations/${props.conversationId}`,
      { params: conversationTypeParam(props.isInvite) }
    );
  },
  getMessages: function ({
    conversationId,
    isInvite,
  }: {
    conversationId: number;
    isInvite?: boolean;
  }) {
    return axios.get<types.UserMessageType[]>(
      `/api9/users/${encodedUsername()}/conversations/${conversationId}/messages`,
      { params: conversationTypeParam(isInvite) }
    );
  },
  sendMessage: function ({
    conversationId,
    message,
    isInvite,
  }: {
    conversationId: number;
    message: string;
    isInvite?: boolean;
  }) {
    return axios.post<types.UserMessageType[]>(
      `/api9/users/${encodedUsername()}/conversations/${conversationId}/messages`,
      { message },
      { params: conversationTypeParam(isInvite) }
    );
  },
  markAsRead: function ({
    conversationId,
    readStatus,
    isInvite,
  }: {
    conversationId: number;
    readStatus: boolean;
    isInvite?: boolean;
  }) {
    return axios.put(
      `/api9/users/${encodedUsername()}/conversations/${conversationId}/read`,
      {
        read: readStatus,
      },
      { params: conversationTypeParam(isInvite) }
    );
  },
  getChangeset: function () {
    return axios.get<types.UserConversationChangeSetViewType>(
      `/api9/users/${encodedUsername()}/conversations/changeset`
    );
  },
};

const Classifieds = {
  get: function () {
    return axios.get(`/api9/classifieds/${encodedUsername()}`);
  },
  create: function ({ listingData }) {
    return axios.post<types.ClassifiedViewType>(
      `/api9/classifieds/${encodedUsername()}`,
      {
        ...listingData,
      }
    );
  },
  postAgain: function ({ classifiedId }) {
    return axios.post(`/api9/classifieds/${encodedUsername()}/${classifiedId}`);
  },
  update: function ({ classifiedId, listingData }) {
    return axios.put(`/api9/classifieds/${encodedUsername()}/${classifiedId}`, {
      ...listingData,
    });
  },
  uploadPrimaryPhoto: function (
    classifiedId: number,
    photoFile: Blob,
    userId: number
  ) {
    const body = new FormData();
    body.append('file', photoFile);
    return axios.post(
      `/api9/photos/docs/${userId}/CLASSIFIED/${classifiedId}`,
      body
    );
  },

  createAdditionalPhotoDescription: function ({ classifiedId, description }) {
    return axios.post(
      `/api9/classifieds/${encodedUsername()}/${classifiedId}/photos`,
      { description }
    );
  },

  uploadAdditionalPhoto: function ({ signedPhotoUrl, photoFile }) {
    const body = new FormData();
    body.append('file', photoFile);
    return axios.post<types.UserImageViewType>(signedPhotoUrl, body);
  },

  getAdditionalPhotos: function (classifiedId: number) {
    return axios.get<types.ClassifiedPhotoViewType[]>(
      `/api9/classifieds/${encodedUsername()}/${classifiedId}/photos`
    );
  },

  deleteAdditionalPhoto: function ({ listingId, photoId }) {
    return axios.delete(
      `/api9/classifieds/${encodedUsername()}/${listingId}/photos/${photoId}`
    );
  },

  getPublic: function ({ classifiedId }) {
    return axios.get(`/api9/classifieds/public/${classifiedId}`);
  },
  getPublicPhotos: function ({ classifiedId }) {
    return axios.get(`/api9/classifieds/public/photos/${classifiedId}`);
  },
};

const Events = {
  create: function ({ body }) {
    return axios.post(`/api9/events/${encodedUsername()}`, { ...body });
  },
  getUserEvents: function () {
    return axios.get<types.EventViewType[]>(
      `/api9/events/${encodedUsername()}`
    );
  },
};

/*
 * ------------------------------
 * Assessments
 * ------------------------------
 */
const Assessments = {
  getAlignment: function () {
    return axios.get(`/api9/users/${encodedUsername()}/alignment`);
  },
  updateAlignment: function ({ personalVisionAnswer, teamExpectationsAnswer }) {
    return axios.put(`/api9/users/${encodedUsername()}/alignment`, {
      personalVisionAnswer,
      teamExpectationsAnswer,
    });
  },
  deleteAlignment: function () {
    return axios.delete(`/api9/users/${encodedUsername()}/alignment`);
  },

  getDisc: function () {
    return axios.get(`/api9/users/${encodedUsername()}/disc`);
  },
  updateDisc: function ({ assessmentResponses }) {
    // ex. [["I", "S", "C", "D"], ["D", "S", "I", C]]
    const discAnswers = assessmentResponses.map((question) =>
      question.map((q) => q.answer)
    );
    return axios.put(`/api9/users/${encodedUsername()}/disc`, discAnswers);
  },
  getEmotionalIntelligence: function () {
    return axios.get(`/api9/users/${encodedUsername()}/emotions`);
  },
  deleteDisc: function () {
    return axios.delete(`/api9/users/${encodedUsername()}/disc`);
  },
  updateEmotionalIntelligence: function ({ questionIdAnswers }) {
    return axios.put(`/api9/users/${encodedUsername()}/emotions`, {
      questionIdAnswers,
    });
  },
  deleteEmotionalIntelligence: function () {
    return axios.delete(`/api9/users/${encodedUsername()}/emotions`);
  },
  getCulture: function () {
    return axios.get(`/api9/users/${encodedUsername()}/culture`);
  },
  getEmployerCulture: function () {
    return axios.get(`/api9/users/${encodedUsername()}/culture-employer`);
  },
  updateCulture: function ({ multipleChoiceAnswers, preferenceAnswers }) {
    return axios.put(`/api9/users/${encodedUsername()}/culture`, {
      multipleChoiceAnswers,
      preferenceAnswers,
    });
  },
  updateEmployerCulture: function ({ multipleChoiceAnswers }) {
    return axios.put(`/api9/users/${encodedUsername()}/culture-employer`, {
      multipleChoiceAnswers,
    });
  },
  deleteCulture: function () {
    return axios.delete(`/api9/users/${encodedUsername()}/culture`);
  },
  deleteEmployerCulture: function () {
    return axios.delete(`/api9/users/${encodedUsername()}/culture-employer`);
  },
  getSatisfaction: function () {
    return axios.get(`/api9/users/${encodedUsername()}/satisfaction`);
  },
  updateSatisfaction: function (assessmentResponses) {
    return axios.put(
      `/api9/users/${encodedUsername()}/satisfaction`,
      assessmentResponses
    );
  },
  deleteSatisfaction: function () {
    return axios.delete(`/api9/users/${encodedUsername()}/satisfaction`);
  },

  getValues: function () {
    return axios.get(`/api9/users/${encodedUsername()}/values`);
  },
  updateValues: function (values: string[]) {
    return axios.put(`/api9/users/${encodedUsername()}/values`, { values });
  },
  deleteValues: function () {
    return axios.delete(`/api9/users/${encodedUsername()}/values`);
  },

  getSkills: function () {
    return axios.get<types.ResumeSkillsViewType>(
      `/api9/users/${encodedUsername()}/skills`
    );
  },
  updateSkills: function ({ skills }: types.ResumeSkillsViewType) {
    return axios.put<types.ResumeSkillsViewType>(
      `/api9/users/${encodedUsername()}/skills`,
      {
        skills,
      }
    );
  },
  deleteSkills: function () {
    return axios.delete(`/api9/users/${encodedUsername()}/skills`);
  },
  updateOnboardingSurvey: function (props: EmployerProfileAssessmentViewType) {
    return axios.put<EmployerProfileAssessmentResponseViewType>(
      `/api9/users/employers/${encodedUsername()}/profile/assessment`,
      props
    );
  },
  deleteOnboardingSurvey: function () {
    return axios.delete(
      `/api9/users/employers/${encodedUsername()}/profile/assessment`
    );
  },
};

/*
 * ------------------------------
 * Contact
 * ------------------------------
 */
const Contact = {
  submit: function ({
    body,
    isAuthenticated,
  }: {
    body: ContactUsPayloadType;
    isAuthenticated: boolean;
  }) {
    if (isAuthenticated) {
      return axios.post<null>(`/api9/contactus/${encodedUsername()}`, body);
    } else {
      return axios.post<null>(`/api9/contactus`, body);
    }
  },
};

/*
 * ------------------------------
 * Lists
 * ------------------------------
 */
const Lists = {
  getDiscQuestions: function () {
    return axios.get(`/api9/lists/disc-questions`);
  },

  getEmotionalIntelligenceQuestions: function () {
    return axios.get(`/api9/lists/emotional-intelligence-questions`);
  },

  getCoreValues: function () {
    return axios.get<types.ValuesAssessmentType>(`/api9/lists/values`);
  },

  getCultureMultipleChoiceQuestions: function () {
    return axios.get(`/api9/lists/culture-multiple-choice-questions`);
  },

  getCulturePreferenceQuestions: function () {
    return axios.get(`/api9/lists/culture-preference-questions`);
  },

  getEmployerCultureMultipleChoiceQuestions: function () {
    return axios.get(`/api9/lists/employer-culture-multiple-choice-questions`);
  },

  getSatisfactionQuestions: function () {
    return axios.get(`/api9/lists/job-satisfaction-questions`);
  },

  getSkillCategories: function () {
    return axios.get<types.SkillCategoryType[]>(`/api9/lists/skill-cats`);
  },

  getSkills: function () {
    return axios.get<types.SkillType[]>(`/api9/lists/skills`);
  },

  getEmployerSkillCategories: function () {
    return axios.get<types.SkillCategoryType[]>(
      `/api9/lists/skill-cats-employer`
    );
  },

  getEmployerSkills: function () {
    return axios.get<types.SkillType[]>(`/api9/lists/skills-employer`);
  },
  getCandidateSkills: function () {
    return axios.get<types.SkillType[]>(`/api9/lists/skills-staff`);
  },
  getCandidateTypeQuestions: function () {
    return axios.get<MultiChoiceQuestionType[]>(`/api9/lists/match-questions`);
  },
};

const JDExtractor = {
  parseDocument: function (file: File) {
    const body = new FormData();
    body.append('file', file);
    return axios.post<JDExtractorResponse>(
      `/api9/parse/document/${encodedUsername()}`,
      body,
      serverlessAxiosRequestConfig
    );
  },
  parseText: function (text: string) {
    return axios.post<JDExtractorResponse>(
      `/api9/parse/text/${encodedUsername()}`,
      { text },
      serverlessAxiosRequestConfig
    );
  },
};

/*
 * ------------------------------
 * Salary (dp-salary-api; infra-routed under /api9/salary/* like JDExtractor's
 * /api9/parse/*, so it rides the same base URL + JWT)
 * ------------------------------
 */
const Salary = {
  compliance: function ({ zip }: { zip: string }) {
    return axios.get<ComplianceResponse>(`/api9/salary/compliance`, {
      params: { zip },
    });
  },
  benchmarks: function ({
    role,
    zip,
    payBasis,
  }: {
    role: string;
    zip?: string;
    payBasis?: 'annual' | 'hourly';
  }) {
    return axios.get<BenchmarkResponse>(`/api9/salary/benchmarks`, {
      params: { role, zip, payBasis },
    });
  },
};

export default {
  Assessments,
  Auth,
  Classifieds,
  Contact,
  Conversations,
  Employer,
  Events,
  User,
  Jobs,
  JDExtractor,
  Salary,
  Lists,
  Resumes,
  CandidateMatching,
  Staff,
};
